// components/ChatPanel.jsx - Updated for independent scrolling
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Videocam as VideocamIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const ChatPanel = ({ chat, messages, onSendMessage, loading }) => {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const [sending, setSending] = useState(false);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message handler
  const handleSend = async () => {
    if (newMessage.trim()) {
      setSending(true);
      try {
        await onSendMessage(newMessage);
        setNewMessage('');
      } catch (error) {
        console.error('Failed to send message:', error);
      } finally {
        setSending(false);
      }
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.timestamp).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden', // Prevent the entire panel from scrolling
    }}>
      {/* Chat header - Fixed */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Avatar src={chat?.avatar} sx={{ mr: 2, bgcolor: 'primary.main' }}>
          {chat?.name?.slice(0, 2).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">{chat?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {chat?.status === 'online'
              ? 'Online now'
              : 'Offline'}
            {chat?.isGroup && ` • ${chat?.participants?.length || 0} participants`}
          </Typography>
        </Box>
        <IconButton>
          <PhoneIcon />
        </IconButton>
        <IconButton>
          <VideocamIcon />
        </IconButton>
      </Box>

      {/* Messages area - Scrollable */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', // Only this section scrolls independently
        p: 2, 
        bgcolor: 'background.default',
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start the conversation by sending a message
            </Typography>
          </Box>
        ) : (
          Object.keys(groupedMessages).map(date => (
            <Box key={date}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  my: 2,
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    bgcolor: 'grey.300', 
                    px: 2, 
                    py: 0.5, 
                    borderRadius: 4,
                    color: 'text.secondary' 
                  }}
                >
                  {date === new Date().toLocaleDateString() 
                    ? 'Today' 
                    : date === new Date(Date.now() - 86400000).toLocaleDateString() 
                      ? 'Yesterday' 
                      : date
                  }
                </Typography>
              </Box>
              
              {groupedMessages[date].map((msg) => {
                const isSent = msg.sender.isCurrentUser;
                return (
                  <Box
                    key={msg.id}
                    sx={{
                      display: 'flex',
                      justifyContent: isSent ? 'flex-end' : 'flex-start',
                      mb: 1.5,
                    }}
                  >
                    {!isSent && (
                      <Avatar 
                        src={msg.sender.avatar} 
                        sx={{ mr: 1, width: 36, height: 36, alignSelf: 'flex-end', bgcolor: 'secondary.main' }}
                      >
                        {msg.sender.name?.slice(0, 2).toUpperCase()}
                      </Avatar>
                    )}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        maxWidth: '65%',
                        bgcolor: isSent ? 'primary.main' : 'grey.100',
                        color: isSent ? 'primary.contrastText' : 'text.primary',
                        borderRadius: isSent
                          ? '16px 0 16px 16px'
                          : '0 16px 16px 16px',
                      }}
                    >
                      <Typography variant="body2">{msg.content}</Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: isSent ? 'flex-end' : 'flex-start',
                        alignItems: 'center',
                        mt: 0.5
                      }}>
                        <Typography
                          variant="caption"
                          color={isSent ? 'inherit' : 'text.secondary'}
                        >
                          {formatDistanceToNow(new Date(msg.timestamp), { addSuffix: true })}
                        </Typography>
                        
                        {isSent && (
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                            {msg.status === 'failed' ? (
                              <ErrorIcon color="error" fontSize="small" />
                            ) : (
                              <Typography variant="caption">
                                {msg.status === 'read' 
                                  ? ' ✓✓' 
                                  : msg.status === 'delivered' 
                                  ? ' ✓✓' 
                                  : msg.status === 'sent' 
                                  ? ' ✓' 
                                  : msg.status === 'sending'
                                  ? '...' 
                                  : ''}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message input - Fixed */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <IconButton disabled={loading}>
          <AttachFileIcon />
        </IconButton>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          variant="outlined"
          disabled={loading || sending}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
            }
          }}
        />
        <IconButton disabled={loading || sending}>
          <EmojiEmotionsIcon />
        </IconButton>
        <IconButton 
          color="primary" 
          onClick={handleSend} 
          disabled={loading || sending || !newMessage.trim()}
          sx={{ 
            bgcolor: (newMessage.trim() && !loading && !sending) ? 'primary.main' : 'grey.300',
            color: (newMessage.trim() && !loading && !sending) ? 'white' : 'text.disabled',
            '&:hover': {
              bgcolor: (newMessage.trim() && !loading && !sending) ? 'primary.dark' : 'grey.300',
            }
          }}
        >
          {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatPanel;